import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Clock3, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { api } from "@/lib/api";
import { SCHOOL } from "@/data/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Calendly inline widget — load its script on mount so it picks up the widget div.
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    return () => {
      document.body.removeChild(s);
    };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we reply within two business days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: Mail, title: "Email us", value: SCHOOL.email, testid: "contact-email-card" },
    { icon: MapPin, title: "Find us", value: SCHOOL.address, testid: "contact-address-card" },
    { icon: Clock3, title: "Office hours", value: SCHOOL.hours, testid: "contact-hours-card" },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact"
        lines={[<>Connect with an authority,</>, <span className="font-serif italic font-medium text-brand">not a call centre</span>]}
        testid="contact-page-heading"
      />

      <section className="container-x pb-20 pt-12 sm:pb-28 sm:pt-16" data-testid="contact-section">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.07} className="h-full">
              <div className="card-line h-full p-6" data-testid={c.testid}>
                <c.icon className="h-5 w-5 text-brand" />
                <h2 className="mt-4 font-display text-sm font-bold text-ink">{c.title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.value}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <div className="card-line p-6 sm:p-10" data-testid="contact-form-card">
                {sent ? (
                  <div className="py-10 text-center" data-testid="contact-success-panel">
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-aqualight text-aqua">
                      <Send className="h-6 w-6" />
                    </span>
                    <h3 className="mt-6 font-display text-2xl font-extrabold text-ink">Message received.</h3>
                    <p className="mt-3 text-sm text-slate-600">An admissions advisor will write back within two business days.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
                    <div>
                      <label className="field-label">Your name *</label>
                      <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="field" placeholder="Jordan Meyer" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="field-label">Email *</label>
                      <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="field" placeholder="you@email.com" data-testid="contact-email-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label">Subject</label>
                      <select value={form.subject} onChange={(e) => set("subject", e.target.value)} className="field" data-testid="contact-subject-select">
                        <option value="">Choose a topic…</option>
                        <option>Admissions & applications</option>
                        <option>Course selection & prerequisites</option>
                        <option>Tuition & scholarship</option>
                        <option>International students</option>
                        <option>Something else</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label">Message *</label>
                      <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="field" placeholder="How can we help?" data-testid="contact-message-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="btn-accent w-full disabled:opacity-60 sm:w-auto">
                        {sending ? "Sending…" : <><Send className="h-4 w-4" /> Send message</>}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-x pb-20 sm:pb-28" data-testid="meeting-booking-section">
        <SectionHeading
          align="center"
          eyebrow="Meeting Booking"
          title={<>Book a meeting with <span className="font-serif italic font-medium text-brand">our admissions team</span></>}
          desc="Choose a time, share your questions, and let's tackle your next steps together."
        />
        <Reveal className="mt-10">
          <div className="card-line overflow-hidden bg-white p-2" data-testid="meeting-booking-box">
            <div className="calendly-inline-widget" data-url="https://calendly.com/egos/15min" style={{ minWidth: "320px", height: "700px" }} />
          </div>
        </Reveal>
      </section>
    </>
  );
}
